import * as yup from "yup";
import { useTranslation } from "react-i18next";

export const appl4ParentValidationSchema = () => {
  const { t } = useTranslation();
  const regexPattern = /^[0-9]*(\.)?[0-9]{0,2}$/;
  const minDate = new Date("1900-01-01");
  const numberField = yup
    .string()
    .matches(
      regexPattern,
      t("Το πεδίο πρέπει να είναι αριθμός της μορφής 150 ή 150.00"),
    );

  const negativeNumberField = yup
    .string()
    .matches(
      /^(-)?[0-9]+(\.)?[0-9]{0,2}$/,
      t("Το πεδίο πρέπει να είναι αριθμός, π.χ. 150, -150, 150.00 ή -150.00"),
    );

  return yup
    .object()
    .shape({
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      boardDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900"))
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      totalActive: numberField.required(t("Το πεδίο είναι υποχρεωτικό")),
      businessCircle: negativeNumberField.required(
        t("Το πεδίο είναι υποχρεωτικό"),
      ),
      employees: numberField.required(t("Το πεδίο είναι υποχρεωτικό")),
      assets: numberField.nullable(),
      reserves: numberField.nullable(),
      requirements: numberField.nullable(),
      prepayments: numberField.nullable(),
      equityTotal: negativeNumberField.nullable(),
      obligationsTotal: numberField.nullable(),
      resultsNoTax: negativeNumberField.nullable(),
      fudocWeb: yup.string().nullable(),
      webDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable(),
      webURL: yup.string().nullable(),
      paperDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .nullable(),
      paperName: yup.string().nullable(),
      fudocPaper: yup.string().nullable(),
      usageFromDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .max(
          new Date("2016-07-07"),
          t(
            "Η ημερομηνία λήξης χρήσης δεν μπορεί να είναι μετά την 07/07/2016.",
          ),
        )
        .test(
          "is-before",
          t(
            "Η ημερομηνία 'Ισολογισμού Χρήσης Από' πρέπει να είναι πριν από την ημερομηνία 'Ισολογισμού Χρήσης Έως'.",
          ),
          function (value) {
            const { usageToDate } = this.parent;
            if (
              value &&
              usageToDate &&
              new Date(value) > new Date(usageToDate)
            ) {
              return false;
            }
            return true;
          },
        ),
      usageToDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .max(
          new Date("2016-07-07"),
          t(
            "Η ημερομηνία λήξης χρήσης δεν μπορεί να είναι μετά την 07/07/2016.",
          ),
        ),
      fudocShortEconomicData: yup
        .string()
        .nullable()
        .when(["internationalAccounting"], {
          is: (global: any) => !!global,
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocAnnualEkthesi: yup
        .string()
        .nullable()
        .when(["internationalAccounting"], {
          is: (internationalAccounting: string) => {
            return !!internationalAccounting;
          },
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocProsartima: yup
        .string()
        .nullable()
        .when(
          ["internationalAccounting", "singleFile"],
          ([internationalAccounting, singleFile], schema) => {
            if (!!internationalAccounting || !!singleFile) {
              return schema.notRequired();
            } else {
              return schema.required(t("Το πεδίο είναι υποχρεωτικό"));
            }
          },
        ),
      fudocIsologismos: yup
        .string()
        .nullable()
        .when(["internationalAccounting", "singleFile", "category"], {
          is: (
            internationalAccounting: string,
            singleFile: string,
            category: string,
          ) => {
            return !!category && !internationalAccounting && !singleFile;
          },
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocEkthesiElegkton: yup
        .string()
        .nullable()
        .when(["internationalAccounting", "category"], {
          is: (internationalAccounting: string, category: string) =>
            !internationalAccounting && ["medium", "big"].includes(category),
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocOikonomikesKatastasis: yup
        .string()
        .nullable()
        .when(["internationalAccounting", "singleFile"], {
          is: (internationalAccounting: string, singleFile: string) =>
            !internationalAccounting && !!singleFile,
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocKatastasiApotelesmaton: yup
        .string()
        .nullable()
        .when(["internationalAccounting", "singleFile", "category"], {
          is: (
            internationalAccounting: string,
            singleFile: string,
            category: string,
          ) => {
            return !!category && !internationalAccounting && !singleFile;
          },
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
      fudocEkthesi: yup
        .string()
        .nullable()
        .when(["internationalAccounting", "singleFile", "category"], {
          is: (
            internationalAccounting: string,
            singleFile: string,
            category: string,
          ) =>
            !internationalAccounting &&
            ((!!singleFile && category !== "very small") ||
              (!singleFile && ["small", "medium", "big"].includes(category))),
          then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          otherwise: (schema) => schema,
        }),
    })

    .test(
      "has filled all of website",
      t(
        "Εάν επιθυμείτε να εισάγετε τα στοιχεία της ιστοσελίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
      ),
      function (value) {
        if (!value || typeof value !== "object") {
          return true;
        }

        const fudocWeb = value.fudocWeb?.trim() || null;
        const webDate = value.webDate ?? null;
        const webURL = value.webURL?.trim() || null;

        if (!fudocWeb && !webDate && !webURL) {
          return true;
        }

        let hasError = false;

        if (!fudocWeb) {
          hasError = true;
          this.createError({
            path: "fudocWeb",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της ιστοσελίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }
        if (!webDate) {
          hasError = true;
          this.createError({
            path: "webDate",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της ιστοσελίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }
        if (!webURL) {
          hasError = true;
          this.createError({
            path: "webURL",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της ιστοσελίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }

        return hasError ? false : true;
      },
    )

    .test(
      "has filled all of paper",
      t(
        "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
      ),
      function (value) {
        if (!value || typeof value !== "object") {
          return true;
        }

        const fudocPaper = value.fudocPaper?.trim() || null;
        const paperDate = value.paperDate ?? null;
        const paperName = value.paperName?.trim() || null;

        if (!fudocPaper && !paperDate && !paperName) {
          return true;
        }

        let hasError = false;

        if (!fudocPaper) {
          hasError = true;
          this.createError({
            path: "fudocPaper",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }
        if (!paperDate) {
          hasError = true;
          this.createError({
            path: "paperDate",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }
        if (!paperName) {
          hasError = true;
          this.createError({
            path: "paperName",
            message: t(
              "Εάν επιθυμείτε να εισάγετε τα στοιχεία της εφημερίδας, πρέπει να συμπληρώσετε όλα τα αντίστοιχα πεδία.",
            ),
          });
        }

        return hasError ? false : true;
      },
    );
};
